import {convertVolumeQuantity, type LocationContext} from "@ots-frontend/ots-data";

/** How full a unit is, as the ring badge on its marker draws it; no badge when the contents aren't known. */
export type UnitLoadRing = {
  /** Quantity over capacity, 0 when empty; above 1 when over capacity. */
  fill: number;
  category?: "WATER" | "OIL";
  /** The quantity is the server's estimate, not a reading; the arc is dashed. */
  estimated: boolean;
};

/** Contents worked out longer than this before the ping belong to an earlier load. */
export const UNIT_LOAD_RING_MAX_LAG_MS = 5 * 60_000;
/** From here the arc darkens, as the shift tray's bar does. */
export const UNIT_LOAD_RING_NEAR_CAPACITY = 0.9;

const finite = (value: number | undefined): value is number => value != null && Number.isFinite(value);

/**
 * The ring for a unit's latest ping. It needs a quantity and a capacity in compatible units, worked out alongside the
 * ping, so a dimmed marker keeps the ring it was last seen with. Unknown contents get no ring rather than a guess.
 */
export const unitLoadRing = (ctx: LocationContext): UnitLoadRing | undefined => {
  const contents = ctx.meta?.contents;
  const pingedAt = ctx.location?.dttm;
  if (!contents || contents.state === "UNKNOWN" || !finite(pingedAt)) return undefined;
  const calculatedAt = contents.calculatedDttm;
  if (!finite(calculatedAt) || calculatedAt > pingedAt + UNIT_LOAD_RING_MAX_LAG_MS || pingedAt - calculatedAt > UNIT_LOAD_RING_MAX_LAG_MS) {
    return undefined;
  }
  const category = contents.materialType?.toUpperCase() === "MIXED"
    ? undefined
    : contents.category === "WATER" || contents.category === "OIL" ? contents.category : undefined;
  if (contents.state === "EMPTY") return {fill: 0, category, estimated: contents.estimated};

  const {quantity, quantityUom, capacityUom} = contents;
  if (!finite(quantity) || quantity < 0 || !quantityUom || !finite(contents.capacity) || contents.capacity <= 0 || !capacityUom) {
    return undefined;
  }
  let capacity: number | undefined;
  try {
    capacity = convertVolumeQuantity(capacityUom, quantityUom, contents.capacity);
  } catch {
    // Units that don't convert, e.g. barrels against tons, leave the fill unknown.
    return undefined;
  }
  if (!finite(capacity) || capacity <= 0) return undefined;
  return {fill: quantity / capacity, category, estimated: contents.estimated};
};

// Fixed colors: the badge is an image over map imagery, so it can't read the theme. Oil uses the slate fill rather than
// its near-black, which disappears on satellite.
const RING_COLORS = {
  WATER: {fill: "#22a7f0", near: "#0369a1"},
  OIL: {fill: "#64748b", near: "#334155"},
  NONE: {fill: "#64748b", near: "#334155"},
};
const TRACK_COLOR = "#e2e8f0";
const OVER_COLOR = "#dc2626";

/** The arc's color: the cargo's, darker near capacity, red over it. */
export const unitLoadRingColor = ({fill, category}: UnitLoadRing) => {
  if (fill > 1) return OVER_COLOR;
  const colors = RING_COLORS[category ?? "NONE"];
  return fill >= UNIT_LOAD_RING_NEAR_CAPACITY ? colors.near : colors.fill;
};

/** A corner badge, the size of the site markers' load counts. */
export const UNIT_LOAD_RING_SIZE = 20;
const STROKE = 4;

/**
 * The badge as standalone SVG markup: a white disc so it reads over any imagery, a gray track, then the arc clockwise
 * from the top. Empty is the bare track; an estimate is dashed.
 */
export const unitLoadRingSvg = (ring: UnitLoadRing, size = UNIT_LOAD_RING_SIZE) => {
  const center = size / 2;
  const radius = center - 1 - STROKE / 2 - 1.5;
  const circle = (attributes: string) =>
    `<circle cx="${center}" cy="${center}" r="${radius}" fill="none" ${attributes}/>`;
  const disc = `<circle cx="${center}" cy="${center}" r="${center - 0.5}" fill="#ffffff" stroke="#94a3b8" stroke-width="1"/>`;
  const track = circle(`stroke="${TRACK_COLOR}" stroke-width="${STROKE}"`);
  if (ring.fill <= 0) return svg(size, disc + track);
  const percent = Math.min(ring.fill, 1) * 100;
  // pathLength makes the dash lengths percentages; the rotation starts the arc at the top.
  const arc = circle(
    `stroke="${unitLoadRingColor(ring)}" stroke-width="${STROKE}" pathLength="100" stroke-dasharray="${percent} 100" transform="rotate(-90 ${center} ${center})"`,
  );
  if (!ring.estimated) return svg(size, disc + track + arc);
  // Dashes and the fill both need a dash array, so a dashed mask cuts the arc.
  const mask = `<mask id="d"><rect width="${size}" height="${size}" fill="black"/>${circle(`stroke="white" stroke-width="${STROKE + 2}" pathLength="40" stroke-dasharray="2 1.2"`)}</mask>`;
  return svg(size, `<defs>${mask}</defs>${disc}${track}<g mask="url(#d)">${arc}</g>`);
};

const svg = (size: number, body: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${body}</svg>`;

export const unitLoadRingDataUrl = (ring: UnitLoadRing, size = UNIT_LOAD_RING_SIZE) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(unitLoadRingSvg(ring, size))}`;

/** "62% full", "Empty", "Over capacity (112%)", with "about" for an estimate: the ring's accessible name. */
export const unitLoadRingLabel = ({fill, estimated}: UnitLoadRing) => {
  if (fill <= 0) return estimated ? "About empty" : "Empty";
  const percent = `${estimated ? "about " : ""}${Math.round(fill * 100)}%`;
  return fill > 1 ? `Over capacity (${percent})` : `${percent.charAt(0).toUpperCase()}${percent.slice(1)} full`;
};
